import { formatSize, randomId, removeExt } from 'pano-utils';
import { type PanoCheckResult, type PanoSuffix, PanoTaskType } from '@types';
import getFileMd5 from '@utils/getFileMd5';
import getExif from '@utils/getExif';
import { TASK_SIZE_LIMIT } from '../../constants';

export default async function panoImageCheck(
  file: File
): Promise<PanoCheckResult> {
  const image = await loadImage(file);
  const { type, width, height } = getImageType(image);
  if (!type) {
    throw new Error(
      `添加失败: ${file.name} - 错误的图片尺寸 (请使用正方形或2:1的全景图片)`
    );
  }
  const sizeLimit = TASK_SIZE_LIMIT[type];
  if (file.size > sizeLimit) {
    const typeName =
      type === PanoTaskType.group ? '六面体全景每张' : '单张全景';
    throw new Error(
      `添加失败: ${file.name} - 图片过大 (${typeName}不能大于${formatSize(
        sizeLimit
      )})`
    );
  }
  const result = await formatFile(file, type);
  window.URL.revokeObjectURL(image.src);
  return { ...result, src: image.src, width, height };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => {
      reject(
        new Error(
          `添加失败: ${file.name} - 图片加载出错, 请尝试更换或重新压制图片`
        )
      );
    };
    image.src = window.URL.createObjectURL(file);
  });
}

function getImageType(image: HTMLImageElement) {
  const width = image.naturalWidth;
  const height = image.naturalHeight;
  const ratio = width / height;
  const type = (() => {
    if (ratio === 2) return PanoTaskType.single;
    if (ratio === 1) return PanoTaskType.group;
  })();
  return { type, width, height };
}

async function formatFile(file: File, type: PanoTaskType) {
  const md5 = await getFileMd5(file);
  if (!md5) {
    throw new Error(`添加失败: ${file.name} - 文件解析错误`);
  }
  const { name, suffix } = nameCheck(file.name, type);
  const { exif, gps } = await getExif(file);
  return {
    id: randomId(`${md5}_`),
    file,
    name,
    size: file.size,
    md5: `${md5}${suffix}`,
    suffix,
    type,
    gps,
    exif,
  };
}

const SUFFIX_DICT: Record<string, PanoSuffix> = {
  front: 'f',
  back: 'b',
  left: 'l',
  right: 'r',
  top: 'u',
  bottom: 'd',
  up: 'u',
  down: 'd',
  f: 'f',
  b: 'b',
  l: 'l',
  r: 'r',
  u: 'u',
  d: 'd',
  '1': 'f',
  '3': 'b',
  '4': 'l',
  '2': 'r',
  '5': 'u',
  '6': 'd',
};

function nameCheck(
  filename: string,
  type: PanoTaskType
): { name: string; suffix: PanoSuffix } {
  if (type === PanoTaskType.single) {
    return { name: removeExt(filename), suffix: 'all' };
  }
  const classifySuffix = (suffix: string) => SUFFIX_DICT[suffix];
  const re = /(.*[._])(.*)\.(?:.*?)$/;
  const match = filename.match(re);
  if (match?.[1] && match[2]) {
    const suffix = classifySuffix(match[2]);
    if (suffix) {
      return { name: match[1], suffix };
    }
  }
  throw new Error(`添加失败: ${filename} - 文件命名不符合规范`);
}
